import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { DocumentModel, DocumentPreviewModel } from '@app/data-layer/models/documents';
import { ReplaySubject } from 'rxjs';
import { finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { enumDocumentType } from '@app/data-layer/enums/documents';
import { DocumentsService } from '@services/documents.service';
import { QuickFixStepModel } from '@shared/isf-mutual/interfaces';
import { enumIsfQuickFixStep } from '@shared/isf-mutual/enums';
import { IsfOcrQuickFixService, IsfStateService } from '@shared/isf-mutual/services';
import { IsfModel } from '@app/data-layer/models/isf';
import { enumIsfSubStep } from '@app/modules/isf/enums';
import { IsfOcrQuickFixConsigneeDetailsComponent } from '../isf-ocr-quick-fix-consignee-details/isf-ocr-quick-fix-consignee-details.component';
import { ComponentType } from '@angular/cdk/portal';
import { IsfOcrQuickFixBuyerDetailsComponent } from '../isf-ocr-quick-fix-buyer-details/isf-ocr-quick-fix-buyer-details.component';
import { IsfOcrQuickFixShipToDetailsComponent } from '../isf-ocr-quick-fix-ship-to-details/isf-ocr-quick-fix-ship-to-details.component';
import { IsfOcrQuickFixSupplierDetailsComponent } from '../isf-ocr-quick-fix-supplier-details/isf-ocr-quick-fix-supplier-details.component';
import { IsfOcrQuickFixConsolidatorDetailsComponent } from '../isf-ocr-quick-fix-consolidator-details/isf-ocr-quick-fix-consolidator-details.component';
import { IsfOcrQuickFixCslDetailsComponent } from '../isf-ocr-quick-fix-csl-details/isf-ocr-quick-fix-csl-details.component';
import { IsfOcrQuickFixManufacturerDetailsComponent } from '../isf-ocr-quick-fix-manufacturer-details/isf-ocr-quick-fix-manufacturer-details.component';
import { IsfOcrQuickFixInterHtsCodeDetailsComponent } from '../isf-ocr-quick-fix-inter-hts-code-details/isf-ocr-quick-fix-inter-hts-code-details.component';
import { IsfOcrQuickFixBlDetailsComponent } from '../isf-ocr-quick-fix-bl-details/isf-ocr-quick-fix-bl-details.component';

@Component({
  selector: 'app-isf-ocr-quick-fix-sidebar',
  templateUrl: './isf-ocr-quick-fix-sidebar.component.html',
  styleUrls: ['./isf-ocr-quick-fix-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IsfOcrQuickFixSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public isf: IsfModel;
  @Input() public isfDocuments: DocumentModel[];
  @ViewChild('stepForm', { read: ViewContainerRef, static: false }) public stepForm: ViewContainerRef;

  public previewModalDocs: DocumentPreviewModel[] = [];
  public isOpenDocsPreviewModal: boolean = false;
  public currentPreviewModalDocIndex: number = 0;
  public currentStepProgress: string;

  private stepsToFix: QuickFixStepModel[] = [];
  private currentStepIndex: number = 0;
  private sidebarFormRef: ComponentRef<any> = null;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private documentsService: DocumentsService,
    private isfStateService: IsfStateService,
    private isfOcrQuickFixService: IsfOcrQuickFixService,
  ) {}

  ngOnInit(): void {
    this.syncSteps();
  }

  ngAfterViewInit(): void {
    this.previewModalDocs = this.isfOcrQuickFixService.getCurrentPreviewModalDocs();

    this.setCurrentStepProgress(this.currentStepIndex);
    this.syncStepForm();

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onNext() {
    this.sidebarFormRef.instance.onSubmit();
  }

  public onBack() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.setCurrentStepProgress(this.currentStepIndex);
      const updatedStep: QuickFixStepModel = this.stepsToFix[this.currentStepIndex];
      this.isfOcrQuickFixService.setCurrentStep(updatedStep);

      this.syncStepForm();

      this.cdr.markForCheck();
    } else {
      this.isfOcrQuickFixService.closeSidebar();
    }
  }

  public onPreviewDocument() {
    this.setIsOpenDocsPreviewModal(true);
  }

  public onCloseDocumentPreview() {
    this.setIsOpenDocsPreviewModal(false);
  }

  public onPreviewDocumentDelete(data: { docId: number; shipmentId: number }) {
    const { docId, shipmentId } = data;

    const doc = this.isfDocuments.find((doc) => doc.id === docId);
    if (!doc) {
      return;
    }

    this.deleteDocument(doc, shipmentId);
  }

  public deleteDocument(doc: DocumentModel, shipmentId: number): void {
    if (this.isf?.id) {
      this.deleteDocumentFromServer(doc, shipmentId);
      return;
    }
  }

  public createConsigneeDetailsForm(): void {
    this.createDefaultEntityForm(enumIsfSubStep.CONSIGNEE_DETAILS, 'consigneeFields', IsfOcrQuickFixConsigneeDetailsComponent);
  }

  public createBuyerDetailsForm(): void {
    this.createDefaultEntityForm(enumIsfSubStep.BUYER_DETAILS, 'buyerFields', IsfOcrQuickFixBuyerDetailsComponent);
  }

  public createShipToDetailsForm(): void {
    this.createDefaultEntityForm(enumIsfSubStep.SHIP_TO_DETAILS, 'shipToFields', IsfOcrQuickFixShipToDetailsComponent);
  }

  public createSupplierDetailsForm(): void {
    this.createDefaultEntityForm(enumIsfSubStep.SUPPLIER_DETAILS, 'supplierFields', IsfOcrQuickFixSupplierDetailsComponent);
  }

  public createConsolidatorDetailsForm(): void {
    this.createDefaultEntityForm(enumIsfSubStep.CONSOLIDATOR_DETAILS, 'consolidatorFields', IsfOcrQuickFixConsolidatorDetailsComponent);
  }

  public createContainerStuffingLocationDetailsForm(): void {
    this.createDefaultEntityForm(
      enumIsfSubStep.CONTAINER_STUFFING_LOCATION_DETAILS,
      'stuffingLocationFields',
      IsfOcrQuickFixCslDetailsComponent,
    );
  }

  public createManufacturerDetailsForm(): void {
    this.createDefaultEntityForm(enumIsfSubStep.MANUFACTURER_DETAILS, 'manufacturerFields', IsfOcrQuickFixManufacturerDetailsComponent, [
      'invoiceId',
    ]);
  }

  public createInternationHtsCodeDetailsForm(): void {
    this.createDefaultEntityForm(
      enumIsfSubStep.INTERNATIONAL_HTS_CODE_DETAILS,
      'manufacturerFields',
      IsfOcrQuickFixInterHtsCodeDetailsComponent,
      ['invoiceId'],
    );
  }

  public createBlDetailsForm(): void {
    this.createDefaultEntityForm(enumIsfSubStep.BILL_OF_LADING_DETAILS, '', IsfOcrQuickFixBlDetailsComponent, []);
  }

  public setIsOpenDocsPreviewModal(state: boolean) {
    this.isOpenDocsPreviewModal = state;
  }

  public get entryPreviewModalDocs(): DocumentPreviewModel[] {
    return this.previewModalDocs?.filter((pmd, index, self) => {
      return index === self.findIndex((p) => p.id === pmd.id);
    });
  }

  public get isFirstStep(): boolean {
    return this.currentStepIndex <= 0;
  }

  public get isLastStep(): boolean {
    const totalStepsCount = this.stepsToFix.length;
    return this.currentStepIndex + 1 >= totalStepsCount;
  }

  public get sidebarTitle(): string {
    const currentStepType = this.stepsToFix[this.currentStepIndex].type;
    const stepKeyName = enumIsfQuickFixStep[currentStepType];
    const stepName = `ISF.OCR_QUICK_FIX_SIDEBAR.${[stepKeyName]}.TITLE`;

    return stepName;
  }

  public get nextButtonText() {
    return this.isLastStep ? 'FINISH' : 'LABELS.NEXT';
  }

  private createDefaultEntityForm(
    substep: enumIsfSubStep,
    fieldsAttrName: string = '',
    componentClass: ComponentType<any>,
    stepAttributes: string[] = [],
  ) {
    if (!this.stepForm) {
      return;
    }

    const entityDetails = this.isfOcrQuickFixService.getSidebarSectionFields(substep);

    this.sidebarFormRef = this.stepForm.createComponent(componentClass, {
      injector: this.injector,
    });

    const currentStep = this.isfOcrQuickFixService.getCurrentStep();

    const entityDetailsFormRef: ComponentRef<any> = this.sidebarFormRef;

    //Set current step attributes to the form if any exist
    Object.keys(currentStep).forEach((key) => {
      if (stepAttributes.includes(key)) {
        entityDetailsFormRef.instance[key] = currentStep[key];
      }
    });

    if (!!fieldsAttrName) {
      entityDetailsFormRef.instance[fieldsAttrName] = entityDetails;
    }

    entityDetailsFormRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
      this.Next();
    });

    this.cdr.markForCheck();
  }

  private Next() {
    const stepsToFix = this.isfOcrQuickFixService.getSyncedStepsToFix(this.stepsToFix);
    const totalStepsCount = stepsToFix.length;

    if (this.currentStepIndex + 1 < totalStepsCount) {
      this.currentStepIndex++;
      this.setCurrentStepProgress(this.currentStepIndex);
      const updatedStep: QuickFixStepModel = stepsToFix[this.currentStepIndex];
      this.isfOcrQuickFixService.setCurrentStep(updatedStep);

      this.syncStepForm();

      this.cdr.markForCheck();
    } else {
      this.isfOcrQuickFixService.closeSidebar();
    }

    this.isfOcrQuickFixService.dataSaved$.next();
  }

  private syncSteps() {
    this.stepsToFix = this.isfOcrQuickFixService.getCurrentStepsToFix();
    const updatedStep: QuickFixStepModel = this.stepsToFix[this.currentStepIndex];
    this.isfOcrQuickFixService.setCurrentStep(updatedStep);
  }

  private syncStepForm() {
    this.stepForm.remove();
    const currentStep = this.isfOcrQuickFixService.getCurrentStep();
    const currentStepType = currentStep.type;
    switch (currentStepType) {
      case enumIsfQuickFixStep.CONSIGNEE_DETAILS:
        this.createConsigneeDetailsForm();
        break;
      case enumIsfQuickFixStep.BUYER_DETAILS:
        this.createBuyerDetailsForm();
        break;
      case enumIsfQuickFixStep.SHIP_TO_DETAILS:
        this.createShipToDetailsForm();
        break;
      case enumIsfQuickFixStep.SUPPLIER_DETAILS:
        this.createSupplierDetailsForm();
        break;
      case enumIsfQuickFixStep.CONSOLIDATOR_DETAILS:
        this.createConsolidatorDetailsForm();
        break;
      case enumIsfQuickFixStep.CONTAINER_STUFFING_LOCATION_DETAILS:
        this.createContainerStuffingLocationDetailsForm();
        break;
      case enumIsfQuickFixStep.MANUFACTURER_DETAILS:
        this.createManufacturerDetailsForm();
        break;
      case enumIsfQuickFixStep.INTERNATIONAL_HTS_CODE_DETAILS:
        this.createInternationHtsCodeDetailsForm();
        break;
      case enumIsfQuickFixStep.BILL_OF_LADING_DETAILS:
        this.createBlDetailsForm();
        break;

      default:
        this.sidebarFormRef = null;
        break;
    }

    this.scrollToFormTop();
  }

  private deleteDocumentFromServer(doc: DocumentModel, shipmentId: number): void {
    this.documentsService
      .deleteDocument(doc.id, false)
      .pipe(
        switchMap(() => this.isfStateService.updateDocuments(true)),
        switchMap(() => this.isfStateService.loadIsf(this.isf.referenceNumber).pipe(take(1))),
        finalize(() => {
          this.previewModalDocs = this.previewModalDocs.filter((pmd) => pmd.id !== doc.id);
          this.isfOcrQuickFixService.setCurrentPreviewModalDocs(this.previewModalDocs);
          this.cdr.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((isf) => {
        this.isfStateService.updateCurrentIsf(isf);
        this.cdr.detectChanges();
      });
  }

  private setCurrentStepProgress(currentStepIndex: number) {
    const totalStepsCount = this.stepsToFix.length;
    const currentStepNumber = currentStepIndex + 1;
    const currentProgress = currentStepNumber / totalStepsCount;
    this.currentStepProgress = (currentProgress * 100 || 0) + '%';
  }

  private scrollToFormTop(): void {
    this.stepForm.element.nativeElement.scrollTo({
      top: 0,
    });
  }
}
